/**
 * @class predefined filters
 * Sign:
 * const key = ms.loadKeyFromFile('mbox.key', '123456')
 * const cer = ms.loadCertificateFromFile('mbox.cer')
 * ms.sign(msg, key, cer)
 *
 * Verify:
 * const cer = ms.loadCertificateFromFile('root.cer')
 * const verified = ms.verify(msg, cer))
 */

const fs = require('fs')
const path = require('path')
const crypto = require('crypto')
const binary = require('./binary.js')
const ms = module.exports = {}
const SIGN_ALG = 'RSA-SHA256'
const ENCRYPTION_ALG = 'aes-256-cbc'
const ENCRYPTION_HASH = 'sha256'

ms.loadKeyFromFile = function(path, passphrase) {
  const pem = fs.readFileSync(path)
  passphrase = String(passphrase??'')
  const pprefix = typeof global === 'object' ? global.MOSY_PPREFIX : typeof window === 'object' ? window.MOSY_PPREFIX : null
  if(passphrase !== '') passphrase = String(pprefix??'') + passphrase
  try{
    return crypto.createPrivateKey({
      key: pem,
      format: 'pem',
      type: 'pkcs1',
      cipher: ENCRYPTION_ALG,
      passphrase,
    })
  }catch(e) {

  }
} // loadKeyFromFile

ms.loadKeyFromString = function(pem, passphrase) {
  passphrase = String(passphrase??'')
  const pprefix = typeof global === 'object' ? global.MOSY_PPREFIX : typeof window === 'object' ? window.MOSY_PPREFIX : null
  if(passphrase !== '') passphrase = String(pprefix??'') + passphrase
  try{
    return crypto.createPrivateKey({
      key: pem,
      format: 'pem',
      type: 'pkcs1',
      cipher: ENCRYPTION_ALG,
      passphrase,
    })
  }catch(e) {

  }
} // loadKeyFromString

ms.loadCertificateFromFile = function(path) {
  try{
    const pem = fs.readFileSync(path)
    const cer = new crypto.X509Certificate(pem)
    cer.pem_text = new Uint8Array(pem)
    return cer
  }catch(e) {

  }
} // loadCertificateFromFile

ms.loadCertificateFromString = function(pem) {
  try{
    const cer = new crypto.X509Certificate(pem)
    cer.pem_text = new Uint8Array(pem)
    return cer
  }catch(e) {

  }
} // loadCertificateFromString

ms.signMessage = function(msg, key, cert) {
  try{
    const { sender, target, action, params } = msg
    msg = { sender, target, action, params: { ...params } }
    if(!msg.params.event_dt) msg.params.event_dt = Date.now()*1000
    const data = Buffer.from(binary.msg2uint(msg))
    const md = crypto.createSign(SIGN_ALG)
    md.update(data)
    const sign = new Uint8Array(md.sign(key))
    if(cert) msg.params.cert = typeof (cert)==='string' ? cert : cert.pem_text
    msg.params.sign = sign
    return msg
  }catch(e) {

  }
} // signMessage

ms.verifyMessage = function(msg, root_cert) {
  try{
    // create copy of message
    const { sender, target, action, params } = msg
    msg = { sender, target, action, params: { ...params } }

    // extract certificate and signature
    const cert = Buffer.from(params.cert || params.identity?.cert)
    const sign = Buffer.from(params.sign)

    // remove certificate and signature from copy
    delete msg.params.cert
    delete msg.params.sign

    // construct binary form
    const data = Buffer.from(binary.msg2uint(msg))

    // verify provided certificate has signed the message
    const md = crypto.createVerify(SIGN_ALG)
    md.update(data)
    let verified = md.verify(cert, sign)
    if(!verified) return null

    // verify signing certificate's root matches our root
    const x509 = new crypto.X509Certificate(cert)
    if(typeof (root_cert)==='string') {
      root_cert = new crypto.X509Certificate(root_cert)
    }
    verified = x509.checkIssued(root_cert)
    return verified ? msg : null
  }catch(e) {
    return null
  }
} // verifyMessage

ms.encryptionKeyFromPassword = function(pass) {
  try{
    return crypto.createHash(ENCRYPTION_HASH).update(pass).digest()
  }catch(e) {

  }
} // encryptionKeyFromPassword

ms.encryptFile = async function(src_path, dst_path, cryptKey) {
  const cryptIV = crypto.randomBytes(16)
  const encryptor = crypto.createCipheriv(ENCRYPTION_ALG, cryptKey, cryptIV)
  try{
    let buf = await fs.promises.readFile(src_path)
    let ivcrypted = Buffer.concat([
      cryptIV,
      encryptor.update(buf),
      encryptor.final(),
    ])
    await fs.promises.writeFile(dst_path, ivcrypted)
    buf = null
    ivcrypted = null
    return true
  }catch(e) {
    return false
  }
} // encryptFile

ms.decryptFile = async function(src_path, dst_path, cryptKey) {
  try{
    let buf = await fs.promises.readFile(src_path)
    let iv = buf.slice(0, 16)
    let encrypted = buf.slice(16)
    buf = null
    const decryptor = crypto.createDecipheriv(ENCRYPTION_ALG, cryptKey, iv)
    buf = Buffer.concat([
      decryptor.update(encrypted),
      decryptor.final(),
    ])
    iv = null
    encrypted = null
    await fs.promises.writeFile(dst_path, buf)
    buf = null
    return true
  }catch(e) {
    return false
  }
} // decryptFile

ms.readEncryptedBufferMessages = function(buf, cryptKey, filter_function) {
  const messages = []
  let msg = null
  let size = 0
  try{
    let iv = buf.slice(0, 16)
    let encrypted = buf.slice(16)
    buf = null
    const decryptor = crypto.createDecipheriv(ENCRYPTION_ALG, cryptKey, iv)
    buf = Buffer.concat([
      decryptor.update(encrypted),
      decryptor.final(),
    ])
    iv = null
    encrypted = null
    while(buf.length>4) {
      size = (buf[0]<<16) + (buf[1]<<8) + buf[2]
      if(buf.length<size+3) break // incomplete last block
      msg = binary.uint2msg(buf.slice(3, size+3))
      buf = buf.slice(size+3)
      if(msg) {
        if(!filter_function || filter_function(msg.sender, msg.target, msg.action, msg.params)) { messages.push(msg) }
        msg = null
      }
    }
  }catch(e) {
  }
  buf = null
  return messages
} // readEncryptedBufferMessages

ms.readEncryptedFileMessages = async function(file_path, cryptKey, filter_function) {
  let buf = null
  let messages = []
  try{
    buf = await fs.promises.readFile(file_path)
    messages = ms.readEncryptedBufferMessages(buf, cryptKey, filter_function)
  }catch(e) {
  }
  buf = null
  return messages
} // readEncryptedFileMessages

ms.writeEncryptedFileMessages = async function(file_path, cryptKey, messages) {
  if(!Array.isArray(messages)) messages = [messages]
  const cryptIV = crypto.randomBytes(16)
  const encryptor = crypto.createCipheriv(ENCRYPTION_ALG, cryptKey, cryptIV)
  let result
  try{
    await fs.promises.mkdir(path.dirname(file_path), { recursive: true })
    await fs.promises.writeFile(file_path, cryptIV)
    for(const msg of messages) {
      if(!msg || typeof (msg)!=='object') continue
      const block = binary.msg2uint(msg, true)
      await fs.promises.appendFile(file_path, encryptor.update(block))
    }
    await fs.promises.appendFile(file_path, encryptor.final())
    result = true
  }catch(e) {
    result = false
  }
  return result
} // writeEncryptedFileMessages
